import { useMemo, useState } from 'react';
import type { GapAnalysisEntry, GapItem, RequirementKind } from '@dsr/shared-types';
import { useComplianceGap } from '../api/queries/compliance';
import { ApiError } from '../api/client';
import { StatusBadge } from '../components/ui';

// Gap analysis drill-down. One expandable section per jurisdiction × service
// line target; each section lists its requirements as rows bucketed into
// met (✓ green) / expiring (⚠ amber, with due date) / missing (✗ red).
// Driven solely by useComplianceGap. Visual conventions mirror
// CredentialExpiryPage (badges, page-state) and DetailPlannerPage card stacks.

const DAY_MS = 24 * 60 * 60 * 1000;

const SERVICE_LINE_LABEL: Record<string, string> = {
  unarmed_guard: 'Unarmed Guard',
  armed_guard: 'Armed Guard',
  executive_protection: 'Executive Protection',
  private_investigation: 'Private Investigation',
};

const KIND_LABEL: Record<RequirementKind, string> = {
  company_license: 'Company License',
  branch_license: 'Branch License',
  employee_registration: 'Employee Registration',
  qualifying_agent: 'Qualifying Agent',
  insurance: 'Insurance',
  bond: 'Bond',
  firearm_endorsement: 'Firearm Endorsement',
  training_cert: 'Training Cert',
  advertising_rule: 'Advertising Rule',
  uniform_vehicle_rule: 'Uniform / Vehicle',
  local_license: 'Local License',
  foreign_qualification: 'Foreign Qualification',
  none_required: 'None Required',
  other: 'Other',
};

function serviceLabel(sl: string): string {
  return SERVICE_LINE_LABEL[sl] ?? sl.replace(/_/g, ' ');
}

function kindLabel(kind: RequirementKind): string {
  return KIND_LABEL[kind] ?? String(kind).replace(/_/g, ' ');
}

function fmtDate(epoch: number): string {
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function daysUntil(epoch: number, now: number): string {
  const days = Math.round((epoch - now) / DAY_MS);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Today';
  return `${days}d`;
}

type RowState = 'met' | 'missing' | 'expiring';

function entryKey(e: GapAnalysisEntry): string {
  return `${e.jurisdiction.id}-${e.service_line}`;
}

export function GapAnalysisPage(): JSX.Element {
  const { data, isLoading, isError, error } = useComplianceGap();
  const now = Date.now();

  const entries = useMemo<GapAnalysisEntry[]>(() => data?.entries ?? [], [data]);

  // Sections start collapsed; clicking the header toggles. Default-expand any
  // target that has an outright-missing requirement so problems surface first.
  const [open, setOpen] = useState<Record<string, boolean>>({});

  function isOpen(e: GapAnalysisEntry): boolean {
    const k = entryKey(e);
    if (k in open) return open[k]!;
    return e.missing.length > 0;
  }

  function toggle(e: GapAnalysisEntry): void {
    const k = entryKey(e);
    setOpen((prev) => ({ ...prev, [k]: !(k in prev ? prev[k] : e.missing.length > 0) }));
  }

  return (
    <div className="page gap-analysis-page">
      <header className="page-header">
        <div>
          <h1>Gap Analysis</h1>
          <p className="page-sub">
            Requirement-by-requirement coverage for every targeted jurisdiction
            and service line. Expand a target to see what is met, expiring, or
            missing.
          </p>
        </div>
      </header>

      {isLoading && <div className="page-state">Loading gap analysis…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load gap analysis.'}
        </div>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <div className="page-state">No jurisdiction targets defined yet.</div>
      )}

      {!isLoading && !isError && entries.length > 0 && (
        <div className="gap-sections">
          {entries.map((e) => {
            const expanded = isOpen(e);
            return (
              <section
                key={entryKey(e)}
                className={`gap-section dashboard-card ${expanded ? 'is-open' : ''}`}
              >
                <button
                  type="button"
                  className="gap-section-header"
                  onClick={() => toggle(e)}
                  aria-expanded={expanded}
                >
                  <span className="gap-section-title">
                    <span className="gap-juris">{e.jurisdiction.code}</span>
                    <span className="gap-juris-name">{e.jurisdiction.name}</span>
                    <span className="gap-service">{serviceLabel(e.service_line)}</span>
                  </span>
                  <span className="gap-section-counts">
                    <StatusBadge status="active">{e.met.length} met</StatusBadge>
                    <StatusBadge status="pending">
                      {e.expiring_soon.length} expiring
                    </StatusBadge>
                    <StatusBadge status="expired">
                      {e.missing.length} missing
                    </StatusBadge>
                  </span>
                </button>

                {expanded && (
                  <div className="gap-rows">
                    {e.missing.length === 0 &&
                      e.expiring_soon.length === 0 &&
                      e.met.length === 0 && (
                        <p className="page-state">No tracked requirements.</p>
                      )}
                    {e.missing.map((it, i) => (
                      <GapRow key={`m-${i}`} item={it} state="missing" now={now} />
                    ))}
                    {e.expiring_soon.map((it, i) => (
                      <GapRow key={`e-${i}`} item={it} state="expiring" now={now} />
                    ))}
                    {e.met.map((it, i) => (
                      <GapRow key={`ok-${i}`} item={it} state="met" now={now} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface GapRowProps {
  item: GapItem;
  state: RowState;
  now: number;
}

function GapRow({ item, state, now }: GapRowProps): JSX.Element {
  // Marker glyph + tone per state: met ✓ green, expiring ⚠ amber, missing ✗ red.
  const marker = state === 'met' ? '✓' : state === 'expiring' ? '⚠' : '✗';
  const tone = state === 'met' ? 'success' : state === 'expiring' ? 'warn' : 'danger';
  const badgeStatus =
    state === 'met' ? 'active' : state === 'expiring' ? 'pending' : 'expired';

  return (
    <div className={`gap-row gap-row-${tone}`}>
      <span className={`gap-marker gap-marker-${tone}`} aria-hidden="true">
        {marker}
      </span>
      <StatusBadge status="info" className="gap-kind">
        {kindLabel(item.kind)}
      </StatusBadge>
      <span className="gap-summary">
        {item.summary}
        {item.detail && <span className="gap-detail"> — {item.detail}</span>}
      </span>
      {state === 'expiring' && item.due_at != null ? (
        <StatusBadge status={badgeStatus} className="gap-due">
          {fmtDate(item.due_at)} · {daysUntil(item.due_at, now)}
        </StatusBadge>
      ) : (
        <StatusBadge status={badgeStatus} className="gap-status">
          {state === 'met' ? 'Met' : 'Missing'}
        </StatusBadge>
      )}
    </div>
  );
}
